import React, { useEffect, useState } from 'react';
import { Menu, X, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { PeriscanLogo } from './PeriscanLogo';
import { Magnet } from './animations/Magnet';
const navItems = [{
  label: 'Autonomous Security Validation',
  to: '/product'
}, {
  label: 'Frontier Gateway',
  to: '/frontier-gateway'
}, {
  label: 'How It Works',
  to: '/how-it-works'
}, {
  label: 'Use Cases',
  to: '/use-cases'
}, {
  label: 'Pricing',
  to: '/pricing'
}, {
  label: 'About',
  to: '/about'
}];
export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);
  return <>
      {/* Announcement Bar — slides up & fades out on scroll */}
      <motion.div initial={false} animate={{
      y: scrolled ? -40 : 0,
      opacity: scrolled ? 0 : 1
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} style={{
      pointerEvents: scrolled ? 'none' : 'auto'
    }} className="fixed top-0 left-0 right-0 z-[60] bg-[#1a8fd4]/10 border-b border-[#1a8fd4]/20 backdrop-blur-md" data-id="element-677">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-2 flex items-center justify-center gap-3 text-xs" data-id="element-678">
          <span className="font-mono text-[10px] text-[#1a8fd4] tracking-widest uppercase" data-id="element-679">
            2026
          </span>
          <span className="text-white/80" data-id="element-680">
            Periscan is coming back. Founded in 2004. Reborn in 2026.
          </span>
          <Link to="/early-access" className="hidden sm:inline-flex items-center gap-1 text-[#1a8fd4] hover:text-white transition-colors font-medium" data-id="element-681">
            Join Early Access
            <ArrowRight className="w-3 h-3" data-id="element-682" />
          </Link>
        </div>
      </motion.div>

      {/* ── Top-of-page header row (logo + nav + CTAs) ─────────────────────
                  Visible only at the top of the page. Fades / slides up entirely
                  when the user scrolls — replaced by the floating hamburger below. */}
      <motion.header initial={false} animate={{
      y: scrolled ? -20 : 0,
      opacity: scrolled ? 0 : 1
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} style={{
      pointerEvents: scrolled ? 'none' : 'auto'
    }} className="fixed top-9 left-0 right-0 z-50 bg-transparent" data-id="element-683">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between" data-id="element-684">
          {/* Logo */}
          <Link to="/" className="flex items-center z-10" data-id="element-685">
            <PeriscanLogo variant="mark" size={32} markColor="#1a8fd4" className="h-8 w-auto" data-id="element-686" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-0.5" data-id="element-687">
            {navItems.map(item => <NavLink key={item.label} to={item.to} className={({
            isActive
          }) => `relative text-sm transition-colors px-2.5 xl:px-3 py-2 rounded-md hover:bg-white/5 ${isActive ? 'text-white' : 'text-white/60 hover:text-white'}`} data-id="element-688">
                {({
              isActive
            }) => <>
                    {item.label}
                    {isActive && <motion.span layoutId="nav-underline" className="absolute left-2.5 right-2.5 xl:left-3 xl:right-3 -bottom-px h-px bg-[#1a8fd4]" data-id="element-689" />}
                  </>}
              </NavLink>)}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-2" data-id="element-690">
            <Link to="/early-access" className="text-sm text-white/60 hover:text-white transition-colors px-3 py-2 hidden 2xl:inline-flex" data-id="element-691">
              Validation Snapshot
            </Link>
            <Magnet strength={0.3} data-id="element-692">
              <Link to="/early-access" className="inline-flex items-center gap-1.5 px-3 xl:px-4 py-2 bg-white text-black text-sm font-medium rounded-full hover:bg-white/90 transition-colors whitespace-nowrap" data-id="element-693">
                <span className="hidden xl:inline" data-id="element-694">Request Early Access</span>
                <span className="xl:hidden" data-id="element-695">Early Access</span>
                <ArrowRight className="w-3.5 h-3.5" data-id="element-696" />
              </Link>
            </Magnet>
          </div>

          {/* Mobile hamburger at top (only at top of page, only on mobile) */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-white/80 hover:text-white" aria-label="Toggle menu" data-id="element-697">
            {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-698" /> : <Menu className="w-6 h-6" data-id="element-699" />}
          </button>
        </div>
      </motion.header>

      {/* ── Floating persistent hamburger ──────────────────────────────────
                  Appears only after scroll. No surrounding bar, no full-width chrome.
                  A small frosted pill in the top-right that never blocks content. */}
      <motion.div initial={false} animate={{
      opacity: scrolled ? 1 : 0,
      scale: scrolled ? 1 : 0.85,
      y: scrolled ? 0 : -8
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} style={{
      pointerEvents: scrolled ? 'auto' : 'none'
    }} className="fixed top-4 right-4 md:top-6 md:right-6 z-[55]" data-id="element-700">
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="group relative inline-flex items-center justify-center w-11 h-11 rounded-full bg-black/55 backdrop-blur-xl border border-white/15 text-white/85 hover:text-white hover:bg-black/70 hover:border-white/25 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.6)] transition-colors" aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} data-id="element-701">
          <AnimatePresence mode="wait" initial={false} data-id="element-702">
            {mobileMenuOpen ? <motion.span key="close" initial={{
            rotate: -90,
            opacity: 0
          }} animate={{
            rotate: 0,
            opacity: 1
          }} exit={{
            rotate: 90,
            opacity: 0
          }} transition={{
            duration: 0.15
          }} className="absolute inset-0 flex items-center justify-center" data-id="element-703">
                <X className="w-5 h-5" data-id="element-704" />
              </motion.span> : <motion.span key="menu" initial={{
            rotate: 90,
            opacity: 0
          }} animate={{
            rotate: 0,
            opacity: 1
          }} exit={{
            rotate: -90,
            opacity: 0
          }} transition={{
            duration: 0.15
          }} className="absolute inset-0 flex items-center justify-center" data-id="element-705">
                <Menu className="w-5 h-5" data-id="element-706" />
              </motion.span>}
          </AnimatePresence>
        </button>
      </motion.div>

      {/* ── Menu panel — floating dropdown attached to top-right ──────── */}
      <AnimatePresence data-id="element-707">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -8,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -8,
        scale: 0.96
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className={`fixed z-[54] right-4 md:right-6 ${scrolled ? 'top-[4.25rem] md:top-[5rem]' : 'top-[7rem]'} w-[min(22rem,calc(100vw-2rem))] rounded-2xl bg-[#070a0e]/95 backdrop-blur-xl border border-white/10 shadow-[0_24px_64px_-16px_rgba(0,0,0,0.7)] overflow-hidden`} data-id="element-708">
            <nav className="flex flex-col p-3" data-id="element-709">
              {navItems.map(item => <NavLink key={item.label} to={item.to} className={({
            isActive
          }) => `relative px-4 py-3 rounded-lg text-base transition-colors ${isActive ? 'text-white bg-white/[0.04]' : 'text-white/75 hover:text-white hover:bg-white/[0.03]'}`} data-id="element-710">
                  {({
              isActive
            }) => <span className="flex items-center justify-between" data-id="element-711">
                      <span data-id="element-712">{item.label}</span>
                      {isActive && <span className="w-1.5 h-1.5 rounded-full bg-[#1a8fd4]" data-id="element-713" />}
                    </span>}
                </NavLink>)}
              <div className="px-2 pt-3 mt-2 border-t border-white/5" data-id="element-714">
                <Link to="/early-access" className="inline-flex w-full items-center justify-center gap-1.5 px-5 py-3 bg-white text-black text-sm font-medium rounded-full hover:bg-white/90 transition-colors" data-id="element-715">
                  Request Early Access
                  <ArrowRight className="w-3.5 h-3.5" data-id="element-716" />
                </Link>
              </div>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </>;
}